import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { AbstractControl, FormGroup, ValidatorFn } from '@angular/forms';
import remove from 'lodash-es/remove';
import moment from 'moment';
import { Observable, Subject } from 'rxjs';
import { Gender } from '../../../../API';
import {
  formatDateAsAWSDateString,
  isRealCalendarDate,
  PatientFormControlType,
  PatientFormGroup
} from '../../../shared/patient-form/patient-form.model';

export enum PatientAttribute {
  'First_Name' = 'First Name',
  'Last_Name' = 'Last Name',
  'Email' = 'Email',
  'Date_Of_Birth' = 'Date Of Birth',
  'HealthCard' = 'Health Card',
  'Gender' = 'Gender',
  'Phone' = 'Phone',
  'Address' = 'Address',
  'Ignore' = 'Ignore'
}
@Injectable({
  providedIn: 'root'
})
export class ImportWizardFileReceivedService {
  private readonly requiredAttributes = [PatientAttribute.First_Name, PatientAttribute.Last_Name];
  private readonly oneOfAttributes = [
    PatientAttribute.Email,
    PatientAttribute.Date_Of_Birth,
    PatientAttribute.Phone
  ];
  public readonly patientAttributeMappings: Map<
    PatientAttribute,
    { formControlType: PatientFormControlType; synonyms: string[] }
  > = new Map();
  constructor(private datePipe: DatePipe) {
    this.patientAttributeMappings.set(PatientAttribute.First_Name, {
      formControlType: PatientFormControlType.FirstName,
      synonyms: ['firstname']
    });
    this.patientAttributeMappings.set(PatientAttribute.Last_Name, {
      formControlType: PatientFormControlType.LastName,
      synonyms: ['lastname', 'surname']
    });
    this.patientAttributeMappings.set(PatientAttribute.Email, {
      formControlType: PatientFormControlType.Email,
      synonyms: ['email', 'emailaddress']
    });
    this.patientAttributeMappings.set(PatientAttribute.Date_Of_Birth, {
      formControlType: PatientFormControlType.DateOfBirth,
      synonyms: ['birthday', 'dateofbirth', 'dob', 'birthdate']
    });
    this.patientAttributeMappings.set(PatientAttribute.HealthCard, {
      formControlType: PatientFormControlType.HealthCard,
      synonyms: [
        'patient Card',
        'patientHealthCard',
        'health Card',
        'Health Card',
        'healthCard',
        'health card',
        'healthcard'
      ]
    });
    this.patientAttributeMappings.set(PatientAttribute.Gender, {
      formControlType: PatientFormControlType.Gender,
      synonyms: ['gender', 'sex']
    });
    this.patientAttributeMappings.set(PatientAttribute.Phone, {
      formControlType: PatientFormControlType.Phone,
      synonyms: ['phone', 'cell', 'cellphone']
    });
    this.patientAttributeMappings.set(PatientAttribute.Address, {
      formControlType: PatientFormControlType.Address,
      synonyms: ['address']
    });
    this.patientAttributeMappings.set(PatientAttribute.Ignore, {
      formControlType: PatientFormControlType.Other,
      synonyms: ['Ignore']
    });
  }

  // Synchronous chunk-builder: callers that already have the logged-in doctor id can build PatientFormGroups
  // for any subset of CSV rows without re-querying staff. Used by the batched import flow to avoid
  // freezing the main thread on large CSVs.
  // Default value for a form control is '', and a control is set to null when a matSelect is
  // assigned the attribute corresponding to it but the cell is empty; if no column corresponds
  // to a control at all, the control keeps its default value.
  public csvRowsToPatientFormGroups(
    csvPatientData: string[][],
    csvPatientColumns: string[],
    matSelectors: FormGroup,
    loggedInDoctorId: string
  ): PatientFormGroup[] {
    return csvPatientData.map(row => {
      const patient: PatientFormGroup = new PatientFormGroup();
      csvPatientColumns.forEach((columnName, index) => {
        const patientAttribute = matSelectors.controls[columnName].value as PatientAttribute;
        const patientAttributeValue = this.formatInputToDatabaseFormat(row[index]);
        if (patientAttribute === PatientAttribute.Gender) {
          // Gender needs the sibling genderOther control, so it is set here where the whole
          // PatientFormGroup is in scope. row[index] keeps the original casing for genderOther.
          this.assignGenderToPatient(patient, patientAttributeValue, row[index]);
          return;
        }
        const patientControlType = this.patientAttributeMappings.get(patientAttribute)
          .formControlType;
        const control = patient.getControl(patientControlType);
        this.assignValueToControl(patientAttribute, patientAttributeValue, control);
      });
      patient.controls.patientDoctorId.setValue(loggedInDoctorId);
      return patient;
    });
  }

  // Should not use contains since we could have something like 'Doctor Last Name' as a column of the csv, and column matching should be strict rather than generous to prevent accidents
  public createControlsConfig(patientsColumns: string[]): any {
    const controlsConfig = {};
    const unusedPatientAttributes = [
      ...Object.keys(PatientAttribute).map(key => PatientAttribute[key])
    ];
    patientsColumns.forEach(originalCsvColumn => {
      const simplifiedColumn = originalCsvColumn.replace(/number|patient|\W/gi, '').toLowerCase();
      const closestMatch = Array.from(this.patientAttributeMappings.keys()).find(key =>
        this.patientAttributeMappings
          .get(key)
          .synonyms.some(synonym => synonym === simplifiedColumn)
      );
      controlsConfig[originalCsvColumn] =
        closestMatch && unusedPatientAttributes.find(val => val === closestMatch)
          ? closestMatch
          : this.patientAttributeMappings.get(PatientAttribute.Ignore).synonyms[0];
      remove(unusedPatientAttributes, val => val === closestMatch);
    });
    return controlsConfig;
  }

  public createControlsValidator(): ValidatorFn {
    return (selectors: FormGroup) => {
      const controls = Object.keys(selectors.controls).map(key => selectors.controls[key]);
      return this.requiredAttributes.every(attr =>
        controls.some(control => control.value === attr)
      ) && this.oneOfAttributes.some(attr => controls.some(control => control.value === attr))
        ? null
        : { missingRequired: '' };
    };
  }

  public getValidEntries(
    patientFormGroups: PatientFormGroup[],
    matSelectors: FormGroup
  ): boolean[][] {
    return patientFormGroups.map(patientFormGroup => {
      const validAttributes = Object.keys(matSelectors.controls).map(() => true);
      for (const patientAttribute of Object.keys(PatientAttribute).map(
        key => PatientAttribute[key]
      )) {
        const patientControlType = this.patientAttributeMappings.get(patientAttribute)
          .formControlType;
        const patientControl = patientFormGroup.getControl(patientControlType);
        if (patientControl && patientControl.invalid) {
          const columnIndex = Object.keys(matSelectors.controls).findIndex(
            key => matSelectors.controls[key].value === patientAttribute
          );
          validAttributes[columnIndex] = false;
        }
      }
      return validAttributes;
    });
  }

  public removeDuplicateOptions(
    value: PatientAttribute,
    indexToNotChange: number,
    matSelectors: FormGroup
  ) {
    let i = 0;
    for (const control of Object.keys(matSelectors.controls).map(
      key => matSelectors.controls[key]
    )) {
      if (i !== indexToNotChange && control.value === value) {
        control.setValue(PatientAttribute.Ignore);
      }
      i++;
    }
  }

  public csvValidator(csv: string[][], headers: string[]): string | null {
    if (csv.length === 0) {
      return 'No data in CSV File';
    }
    const rowLengths = csv.map(row => row.length).sort((a, b) => a - b);
    let median = { medianLength: -1, numberOfRowsWithLength: 0 };
    while (rowLengths.length !== 0) {
      const numberOfRowsOfLast = rowLengths.lastIndexOf(rowLengths[0]) + 1;
      if (numberOfRowsOfLast > median.numberOfRowsWithLength) {
        median = { medianLength: rowLengths[0], numberOfRowsWithLength: numberOfRowsOfLast };
      }
      rowLengths.splice(0, numberOfRowsOfLast);
    }

    if (median.numberOfRowsWithLength !== csv.length) {
      return `Most rows are ${median.medianLength} entries long, but rows (
      ${csv
        .map((row, index) => (row.length !== median.medianLength ? index : null))
        .filter(val => val !== null)
        .toString()}
      ) do not have the same amount of entries.`;
    }

    const duplicates = [];
    for (const header of headers) {
      if (duplicates[header]) {
        return `Cannot import CSV with duplicate headers. Please remove one of the '${header}' headers.`;
      }
      duplicates[header] = true;
    }

    return null;
  }

  public removeCharFromFile(file: File): Observable<File> {
    const fr = new FileReader();
    fr.readAsText(file);
    const done = new Subject<File>();
    fr.onloadend = this.onFileLoad(done, fr);

    return done.asObservable();
  }

  // Exact full-word synonyms for the two known genders, matched against the trimmed+lowercased
  // cell. Matching is exact, never substring — the old `indexOf('f')` mis-mapped values like
  // "prefer not to say" to female. Extend these sets as new CSV spellings show up.
  private readonly femaleGenderSynonyms = new Set([
    'female',
    'f',
    'woman',
    'women',
    'girl',
    'w',
    'fem'
  ]);
  private readonly maleGenderSynonyms = new Set(['male', 'm', 'man', 'men', 'boy', 'guy']);

  // Assigns gender AND its sibling genderOther control. Recognized male/female synonyms map to
  // those; anything else (e.g. "nonbinary", "x", "prefer not to say", a numeric code) maps to
  // Gender.other with the ORIGINAL text preserved in genderOther, so it is neither lost nor
  // silently mis-coerced into male/female.
  private assignGenderToPatient(
    patient: PatientFormGroup,
    normalizedValue: string,
    originalValue: string
  ) {
    if (!normalizedValue) {
      patient.controls.gender.setValue(null);
      patient.controls.genderOther.setValue('');
      return;
    }
    if (this.femaleGenderSynonyms.has(normalizedValue)) {
      patient.controls.gender.setValue(Gender.female);
      patient.controls.genderOther.setValue('');
      return;
    }
    if (this.maleGenderSynonyms.has(normalizedValue)) {
      patient.controls.gender.setValue(Gender.male);
      patient.controls.genderOther.setValue('');
      return;
    }
    patient.controls.gender.setValue(Gender.other);
    patient.controls.genderOther.setValue(
      originalValue && originalValue.trim() ? originalValue.trim() : normalizedValue
    );
  }

  private assignValueToControl(
    patientAttribute: PatientAttribute,
    patientAttributeValue: string,
    control?: AbstractControl
  ) {
    if (!control) {
      return;
    }
    switch (patientAttribute) {
      case PatientAttribute.Date_Of_Birth:
        control.setValue(this.parseDateOfBirthCell(patientAttributeValue));
        break;
      case PatientAttribute.Gender:
        // Handled in csvRowsToPatientFormGroups via assignGenderToPatient (needs the sibling
        // genderOther control), so there is nothing to do on the single control here.
        break;
      case PatientAttribute.Ignore:
        break;
      case PatientAttribute.HealthCard:
        control['controls'].number.setValue(patientAttributeValue);
        break;
      default:
        control.setValue(patientAttributeValue === '' ? null : patientAttributeValue);
    }
  }

  // Empty cell -> null (date of birth is optional). Parseable -> the canonical yyyy-MM-dd
  // storage format. Unparseable or untrustworthy -> the RAW cell text is preserved so the shared
  // dateOfBirth validator flags the row for review, instead of a wrong date being silently saved.
  private parseDateOfBirthCell(dateOfBirthCellValue: string): string | null {
    if (!dateOfBirthCellValue) {
      return null;
    }
    if (/^\d+$/.test(dateOfBirthCellValue)) {
      // Digit-only cells: DatePipe parses these as epoch MILLISECONDS ('19900101' becomes
      // 1970-01-01), silently importing a wrong date. Accept only the compact YYYYMMDD form
      // explicitly; every other digit-only value keeps its raw text and is flagged for review.
      const compactDateParts = dateOfBirthCellValue.match(/^(\d{4})(\d{2})(\d{2})$/);
      return compactDateParts
        ? this.toCanonicalDateOrRawText(
            Number(compactDateParts[1]),
            Number(compactDateParts[2]) - 1,
            Number(compactDateParts[3]),
            dateOfBirthCellValue
          )
        : dateOfBirthCellValue;
    }
    // Dashed dates, with or without an ISO time suffix (lowercase 't'/'z' because the cell was
    // trimmed+lowercased upstream): take the WRITTEN date parts literally. Handing these to
    // DatePipe either rolls impossible dates over (1990-02-30 -> 1990-03-02, defeating the
    // review) or, for timezone-anchored datetimes, shifts the date one day when rendered in
    // local time. A date of birth is a calendar date — the written parts win.
    const dashedDateParts = dateOfBirthCellValue.match(/^(\d{4})-(\d{1,2})-(\d{1,2})([TtZz\s]|$)/);
    if (dashedDateParts) {
      return this.toCanonicalDateOrRawText(
        Number(dashedDateParts[1]),
        Number(dashedDateParts[2]) - 1,
        Number(dashedDateParts[3]),
        dateOfBirthCellValue
      );
    }
    return this.parseDateOfBirthCellWithDatePipe(dateOfBirthCellValue);
  }

  // Canonical zero-padded yyyy-MM-dd when the written parts form a real calendar date, otherwise
  // the raw cell text so the flagged-row review shows the user exactly what the CSV contained.
  private toCanonicalDateOrRawText(
    year: number,
    monthIndex: number,
    dayOfMonth: number,
    rawCellText: string
  ): string {
    return isRealCalendarDate(year, monthIndex, dayOfMonth)
      ? formatDateAsAWSDateString(new Date(year, monthIndex, dayOfMonth))
      : rawCellText;
  }

  // Last resort for free-form cells (slash formats, month names): DatePipe parses them via
  // new Date(...) in LOCAL time, which is day-stable for these shapes. The canonical-shape gate
  // rejects DatePipe "successes" that rolled into nonsense (e.g. negative years).
  private parseDateOfBirthCellWithDatePipe(dateOfBirthCellValue: string): string {
    let formattedDateOfBirth: string = null;
    try {
      formattedDateOfBirth = this.datePipe.transform(dateOfBirthCellValue, 'yyyy-MM-dd');
    } catch (transformError) {
      try {
        formattedDateOfBirth = this.datePipe.transform(
          this.formatDateString(dateOfBirthCellValue),
          'yyyy-MM-dd'
        );
      } catch (fallbackTransformError) {
        formattedDateOfBirth = null;
      }
    }
    const isCanonicalDate =
      !!formattedDateOfBirth && /^\d{4}-\d{2}-\d{2}$/.test(formattedDateOfBirth);
    return isCanonicalDate ? formattedDateOfBirth : dateOfBirthCellValue;
  }

  private onFileLoad(done: Subject<File>, fr: FileReader): () => void {
    return () => {
      // Replaces all '\r' with '\n' because window systems use \r\n as line breaks while unix uses \n, which messes with PapaParse library
      const newFile = new File([(fr.result as string).replace('\r', '\n')], 'modifiedFile.csv');
      done.next(newFile);
    };
  }

  private formatInputToDatabaseFormat(value: string): string {
    return value.trim().toLowerCase();
  }

  private formatDateString(date: string): string {
    const currentYear = new Date().getFullYear() % 100;

    // Could be split by /-, and space
    const dateSplitArray = date.split(/[\.\-\/\s]/);

    if (dateSplitArray.length > 3) {
      return null;
    }
    const dateCombinations = this.getDateCombinations(dateSplitArray);

    // Returning the possible valid date combination
    return dateCombinations.find(dateCombination =>
      // Add the full year because partial year is provided
      moment(
        Number(dateCombination.split(',')[0]) > currentYear
          ? 19 + dateCombination
          : 20 + dateCombination
      ).isValid()
    );
  }

  private getDateCombinations(dateSplitArray: string[]): string[] {
    const combinations: string[] = [];
    for (let i = 0; i < dateSplitArray.length; i++) {
      const firstElement = dateSplitArray[i];
      const remainingElements = dateSplitArray.slice(0, i).concat(dateSplitArray.slice(i + 1));
      combinations.push(`${firstElement}, ${remainingElements[0]}, ${remainingElements[1]}`);
      combinations.push(`${firstElement}, ${remainingElements[1]}, ${remainingElements[0]}`);
    }
    return combinations;
  }
}
